import { useState } from 'react';
import { saveUserPreferences } from '@/services/authService';

interface PreferenceSurveyProps {
  onComplete: () => void;
  onSkip?: () => void;
}

interface Preference {
  type: 'genre' | 'artist';
  value: string;
  familiarityLevel: number;
}

// 常见流派列表
const GENRES = [
  'Electronic', 'Pop', 'Rock', 'Hip Hop', 'R&B',
  'Jazz', 'Classical', 'EDM', 'K-Pop', 'J-Pop',
  'Indie', 'Metal', 'Country', 'Folk', 'Blues'
];

export function PreferenceSurvey({ onComplete, onSkip }: PreferenceSurveyProps) {
  const [preferences, setPreferences] = useState<Preference[]>([]);
  const [selectedType, setSelectedType] = useState<'genre' | 'artist'>('genre');
  const [inputValue, setInputValue] = useState('');
  const [familiarityLevel, setFamiliarityLevel] = useState(3);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const handleAddPreference = () => {
    if (!inputValue.trim()) return;
    
    setPreferences([
      ...preferences,
      {
        type: selectedType,
        value: inputValue.trim(),
        familiarityLevel,
      },
    ]);
    
    setInputValue('');
    setFamiliarityLevel(3);
  };
  
  const handleRemovePreference = (index: number) => {
    setPreferences(preferences.filter((_, i) => i !== index));
  };
  
  const handleSubmit = async () => {
    if (preferences.length === 0) {
      setError('请至少添加一个偏好');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const prefsToSave = preferences.map(pref => ({
        [pref.type]: pref.value,
        familiarityLevel: pref.familiarityLevel,
      }));
      
      await saveUserPreferences(prefsToSave);
      onComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : '保存失败');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="w-full max-w-2xl">
      <h2 className="text-2xl font-bold mb-2 text-center">音乐偏好调查</h2>
      <p className="text-gray-600 text-center mb-6">
        帮助我们了解您对不同流派和艺术家的熟悉程度
      </p>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
        <h3 className="font-medium mb-4">添加偏好</h3>
        
        <div className="space-y-4">
          <div className="flex gap-4">
            <label className="flex items-center">
              <input
                type="radio"
                value="genre"
                checked={selectedType === 'genre'}
                onChange={() => setSelectedType('genre')}
                className="mr-2"
              />
              流派
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                value="artist"
                checked={selectedType === 'artist'}
                onChange={() => setSelectedType('artist')}
                className="mr-2"
              />
              艺术家
            </label>
          </div>
          
          {selectedType === 'genre' ? (
            <select
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500"
            >
              <option value="">选择流派</option>
              {GENRES.map((genre) => (
                <option key={genre} value={genre}>
                  {genre}
                </option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="输入艺术家名称"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500"
            />
          )}
          
          <div>
            <label className="block text-sm font-medium mb-2">
              熟悉程度: {familiarityLevel}/5
            </label>
            <input
              type="range"
              min="1"
              max="5"
              value={familiarityLevel}
              onChange={(e) => setFamiliarityLevel(Number(e.target.value))}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>不熟悉</span>
              <span>非常熟悉</span>
            </div>
          </div>
          
          <button
            onClick={handleAddPreference}
            disabled={!inputValue.trim()}
            className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            添加
          </button>
        </div>
      </div>
      
      {preferences.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <h3 className="font-medium mb-4">已添加的偏好 ({preferences.length})</h3>
          <div className="space-y-2">
            {preferences.map((pref, index) => (
              <div
                key={index}
                className="flex items-center justify-between bg-gray-50 px-4 py-2 rounded"
              >
                <div>
                  <span className="font-medium">{pref.value}</span>
                  <span className="text-gray-500 text-sm ml-2">
                    ({pref.type === 'genre' ? '流派' : '艺术家'})
                  </span>
                  <span className="text-gray-500 text-sm ml-2">
                    熟悉度: {pref.familiarityLevel}/5
                  </span>
                </div>
                <button
                  onClick={() => handleRemovePreference(index)}
                  className="text-red-500 hover:text-red-700 text-sm"
                >
                  删除
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      
      <div className="flex gap-4">
        {onSkip && (
          <button
            onClick={onSkip}
            className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3 px-4 rounded-lg transition-colors"
          >
            跳过
          </button>
        )}
        <button
          onClick={handleSubmit}
          disabled={loading || preferences.length === 0}
          className="flex-1 bg-sky-500 hover:bg-sky-600 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? '保存中...' : '完成'}
        </button>
      </div>
    </div>
  );
}

